import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../ui/card'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Button } from '../ui/button'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select'
import { Loader2, Paperclip, Trash2, LifeBuoy } from 'lucide-react'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'
import SupportEndpoints from '@/endpoints/SupportEndpoints'
import {
    SUPPORT_TICKET_CATEGORY_LABELS,
    SupportTicketAttachmentRequest,
    SupportTicketCategory,
} from '@/model/SupportTicket'

const MAX_ATTACHMENTS = 5
const MAX_BYTES_PER_FILE = 5 * 1024 * 1024 // matches backend cap

interface PendingAttachment extends SupportTicketAttachmentRequest {
    id: string
    sizeBytes: number
}

export const SupportTicketPage: React.FC = () => {
    const navigate = useNavigate()
    const [subject, setSubject] = useState('')
    const [description, setDescription] = useState('')
    const [category, setCategory] = useState<SupportTicketCategory>(
        SupportTicketCategory.BUG
    )
    const [attachments, setAttachments] = useState<PendingAttachment[]>([])
    const [submitting, setSubmitting] = useState(false)

    const handleFileSelection = async (files: FileList | null) => {
        if (!files || files.length === 0) return
        const remainingSlots = MAX_ATTACHMENTS - attachments.length
        if (remainingSlots <= 0) {
            toast.error(`You can attach at most ${MAX_ATTACHMENTS} files.`)
            return
        }

        const toAdd = Array.from(files).slice(0, remainingSlots)
        const next: PendingAttachment[] = []
        for (const f of toAdd) {
            if (f.size > MAX_BYTES_PER_FILE) {
                toast.error(
                    `${f.name} is larger than ${MAX_BYTES_PER_FILE / (1024 * 1024)}MB and was skipped.`
                )
                continue
            }
            try {
                const base64 = await readAsBase64(f)
                next.push({
                    id: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`,
                    filename: f.name,
                    contentType: f.type || 'application/octet-stream',
                    contentBase64: base64,
                    sizeBytes: f.size,
                })
            } catch {
                toast.error(`Could not read ${f.name}.`)
            }
        }
        if (next.length > 0) {
            setAttachments((prev) => [...prev, ...next])
        }
    }

    const handleRemoveAttachment = (id: string) => {
        setAttachments((prev) => prev.filter((a) => a.id !== id))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!subject.trim()) {
            toast.error('Subject is required.')
            return
        }
        if (!description.trim()) {
            toast.error('Description is required.')
            return
        }
        setSubmitting(true)
        try {
            await SupportEndpoints.createTicket({
                subject: subject.trim(),
                description: description.trim(),
                category,
                attachments: attachments.map(
                    ({ filename, contentType, contentBase64 }) => ({
                        filename,
                        contentType,
                        contentBase64,
                    })
                ),
            })
            toast.success('Ticket submitted — thanks for the report.')
            navigate('/')
        } catch (error) {
            toast.error(extractErrorMessage(error, 'Failed to submit ticket'))
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="container mx-auto max-w-3xl px-4 py-6">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <LifeBuoy className="h-5 w-5" />
                        Report an Issue
                    </CardTitle>
                    <CardDescription>
                        Fill out this form to send your report to the EMS team.
                        Attach screenshots or logs if they help explain the issue.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="support-subject">Subject</Label>
                            <Input
                                id="support-subject"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="Short summary"
                                disabled={submitting}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="support-category">Category</Label>
                            <Select
                                value={category}
                                onValueChange={(v) =>
                                    setCategory(v as SupportTicketCategory)
                                }
                                disabled={submitting}
                            >
                                <SelectTrigger id="support-category">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(
                                        SUPPORT_TICKET_CATEGORY_LABELS
                                    ).map(([value, label]) => (
                                        <SelectItem key={value} value={value}>
                                            {label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="support-description">
                                Description
                            </Label>
                            <Textarea
                                id="support-description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={8}
                                placeholder="What were you trying to do? What happened? What did you expect?"
                                disabled={submitting}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Attachments</Label>
                            <div className="flex items-center gap-3">
                                <label
                                    className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                                    aria-disabled={
                                        submitting ||
                                        attachments.length >= MAX_ATTACHMENTS
                                    }
                                >
                                    <Paperclip className="h-4 w-4" />
                                    Add files
                                    <input
                                        type="file"
                                        multiple
                                        className="hidden"
                                        disabled={
                                            submitting ||
                                            attachments.length >= MAX_ATTACHMENTS
                                        }
                                        onChange={(e) => {
                                            handleFileSelection(e.target.files)
                                            e.target.value = ''
                                        }}
                                    />
                                </label>
                                <span className="text-xs text-muted-foreground">
                                    Up to {MAX_ATTACHMENTS} files,{' '}
                                    {MAX_BYTES_PER_FILE / (1024 * 1024)}MB each.
                                </span>
                            </div>
                            {attachments.length > 0 && (
                                <ul className="space-y-1 rounded-md border bg-muted/30 p-2">
                                    {attachments.map((a) => (
                                        <li
                                            key={a.id}
                                            className="flex items-center justify-between gap-2 px-2 py-1 text-sm"
                                        >
                                            <span className="flex-1 truncate">
                                                {a.filename}{' '}
                                                <span className="text-xs text-muted-foreground">
                                                    ({formatBytes(a.sizeBytes)})
                                                </span>
                                            </span>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7 text-destructive hover:text-destructive"
                                                onClick={() =>
                                                    handleRemoveAttachment(a.id)
                                                }
                                                disabled={submitting}
                                                aria-label={`Remove ${a.filename}`}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate(-1)}
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={submitting}>
                                {submitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Submitting...
                                    </>
                                ) : (
                                    'Submit Ticket'
                                )}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    )
}

const readAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
            const result = reader.result as string
            // Strip the data URL prefix so the backend receives raw base64.
            const commaIdx = result.indexOf(',')
            resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result)
        }
        reader.onerror = () => reject(reader.error)
        reader.readAsDataURL(file)
    })
}

const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
